/** Formats a duration as H:MM:SS, or M:SS when under an hour. */
export function formatTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor((totalSeconds / 60) % 60);
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** "4h 12m left" style summary, for library cards. */
export function formatRemaining(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${Math.max(1, m)}m left`;
  return m === 0 ? `${h}h left` : `${h}h ${m}m left`;
}

/**
 * Compares filenames the way a human orders chapters: "Track 2" before
 * "Track 10". A plain lexicographic sort gets that backwards, which would
 * scramble the reading order of most audiobook folders.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
export function naturalCompare(a: string, b: string): number {
  return collator.compare(a, b);
}

const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'm4a',
  'm4b',
  'aac',
  'opus',
  'ogg',
  'oga',
  'flac',
  'wav',
  'wma',
  'mp4',
]);

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot + 1).toLowerCase();
}

/** True for anything we are willing to hand to an <audio> element. */
export function isAudioFile(fileName: string, mimeType?: string): boolean {
  if (mimeType?.startsWith('audio/')) return true;
  return AUDIO_EXTENSIONS.has(extensionOf(fileName));
}

export function isImageFile(fileName: string, mimeType?: string): boolean {
  if (mimeType?.startsWith('image/')) return true;
  return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extensionOf(fileName));
}

/** Strips the extension and any leading "01 - " / "03." track numbering. */
export function prettyTrackName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const base = dot === -1 ? fileName : fileName.slice(0, dot);
  return base.replace(/^\s*\d{1,3}\s*[-._)\]]*\s*/, '').trim() || base;
}

export function stripExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? fileName : fileName.slice(0, dot);
}

/** Deterministic hue from a string, for generated cover tiles. */
export function hueFrom(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

export function initialsOf(title: string): string {
  const words = title.split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}
